import axios from "axios";
import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";
import store from "../../../../core/data/redux/store";
import { tokenService } from "../../../../core/utils/tokenService";

export const ACTION_ADD = "S";
export const ACTION_EDIT = "E";
export const ACTION_FINAL_SUBMIT = "F";
export const ACTION_DELETE = "D";
export const ACTION_NIL = "N";

export const NOT_SPECIFIED = "Not Specified";

export type OfficerDetails = {
  Name: string;
  EmployeeNumber: string;
  BirthDate: string;
  Wing: string;
  Department: string;
  Designation: string;
  PayScale: string;
  BasicPay: string;
  EmploymentType: string;
  JoiningDate: string;
};

export type PropertyRow = {
  __uid: string;
  SequenceNumber: string;
  TypeOfProperty: string;
  NameDetailsAndLocationOfProperty: string;
  DetailsOfProperty: string;
  SizeAndArea: string;
  AcquisitionDateInDDMMYYYYFormatOnly: string;
  AcquisitionPrice: string;
  PresentValue: string;
  OwnerNameWithRelationshipAndShareInPrope: string;
  ModeOfAcquisition: string;
  AnnualIncomeFromProperty: string;
  Remarks: string;
};

let uidCounter = 0;
export const genUid = () => `row_${Date.now()}_${++uidCounter}`;

export const trimVal = (v: any): string => (v == null ? "" : String(v).trim());

export const emptyRow = (): PropertyRow => ({
  __uid: genUid(),
  SequenceNumber: "",
  TypeOfProperty: "",
  NameDetailsAndLocationOfProperty: "",
  DetailsOfProperty: "",
  SizeAndArea: "",
  AcquisitionDateInDDMMYYYYFormatOnly: "",
  AcquisitionPrice: "",
  PresentValue: "",
  OwnerNameWithRelationshipAndShareInPrope: "",
  ModeOfAcquisition: "",
  AnnualIncomeFromProperty: "",
  Remarks: "",
});

export const isBlankPropertyRow = (row: PropertyRow): boolean =>
  !trimVal(row.TypeOfProperty) &&
  !trimVal(row.NameDetailsAndLocationOfProperty) &&
  !trimVal(row.DetailsOfProperty) &&
  !trimVal(row.SizeAndArea) &&
  !trimVal(row.AcquisitionDateInDDMMYYYYFormatOnly) &&
  !trimVal(row.AcquisitionPrice) &&
  !trimVal(row.PresentValue) &&
  !trimVal(row.OwnerNameWithRelationshipAndShareInPrope) &&
  !trimVal(row.ModeOfAcquisition) &&
  !trimVal(row.Remarks);

export const isNilPropertyRow = (row: PropertyRow): boolean =>
  trimVal(row.TypeOfProperty).toUpperCase() === "NIL";

export function formatProfileDate(raw: any): string {
  if (!raw) return "";
  const str = String(raw).trim();
  if (!str) return "";
  const match = str.match(/\/Date\((-?\d+)\)\//);
  if (match) {
    const d = new Date(parseInt(match[1], 10));
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${day}.${month}.${d.getUTCFullYear()}`;
  }
  if (str.length === 8 && !isNaN(Number(str))) {
    return `${str.substring(6, 8)}.${str.substring(4, 6)}.${str.substring(0, 4)}`;
  }
  if (/^\d{2}[.-]\d{2}[.-]\d{4}$/.test(str)) {
    return str.replace(/-/g, ".");
  }
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${day}.${month}.${d.getUTCFullYear()}`;
  }
  return str;
}

export const formatSapDisplayDate = (raw: any): string => formatProfileDate(raw);

export const toSapDate = (d: Date): string =>
  `/Date(${Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())})/`;

export const sapDateToJsDate = (raw: any): Date | null => {
  if (!raw) return null;
  const match = String(raw).match(/\/Date\((-?\d+)\)\//);
  if (!match) return null;
  const d = new Date(parseInt(match[1], 10));
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export function sapDateToInputValue(raw: any): string {
  const js = sapDateToJsDate(raw);
  if (js) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${js.getFullYear()}-${pad(js.getMonth() + 1)}-${pad(js.getDate())}`;
  }
  const str = trimVal(raw);
  const dotted = str.match(/^(\d{2})[.-](\d{2})[.-](\d{4})$/);
  if (dotted) return `${dotted[3]}-${dotted[2]}-${dotted[1]}`;
  return "";
}

export function inputValueToSapDate(value: string): string {
  if (!value) return "";
  const parts = value.split("-").map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return "";
  const [year, month, day] = parts;
  return toSapDate(new Date(year, month - 1, day));
}

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    if (parsed?.details || parsed?.empId || parsed?.usrid) return parsed;
    return parsed?.data?.data?.user || parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

// Header shows this same profile (employeesState.data.data). Its empId is the
// employee currently logged in. details.empId is only a fallback: a previous
// login can leave another employee's number there (for example 00020990).
function getLiveProfile(): any {
  try {
    const data = store.getState()?.employeesState?.data;
    return data?.data || null;
  } catch {
    return null;
  }
}

export function toSapEmpId(value: any): string {
  const raw = trimVal(value);
  if (!raw || raw === NOT_SPECIFIED) return "";
  return /^\d+$/.test(raw) ? raw.padStart(8, "0") : raw;
}

export function getEmpId(user = getLoggedInUser()): string {
  const profile = getLiveProfile();
  return toSapEmpId(
    profile?.empId ||
      user?.empId ||
      profile?.usrid ||
      user?.usrid ||
      user?.emp_id ||
      profile?.details?.empId ||
      user?.details?.empId ||
      profile?.id ||
      user?.id
  );
}

export function getProfileOfficerDetails(user = getLoggedInUser()): OfficerDetails {
  const profileName =
    `${user?.details?.firstname ?? ""} ${user?.details?.lastname ?? user?.details?.lsastname ?? ""}`.trim() ||
    trimVal(user?.details?.fullName) ||
    trimVal(user?.details?.EmployeeName) ||
    trimVal(user?.fullName) ||
    trimVal(user?.name);

  const profileEmpId = getEmpId(user);

  const profileBirthDate = formatProfileDate(
    user?.details?.birthdate ||
      user?.details?.date_of_birth ||
      user?.details?.DateOfBirth ||
      user?.birthdate ||
      user?.dob
  );

  const profileWing =
    trimVal(user?.details?.wing) ||
    trimVal(user?.details?.wing_name) ||
    trimVal(user?.wing) ||
    trimVal(user?.wing_name);

  const profileDept =
    trimVal(user?.details?.sap_department_designation_wings?.department_name) ||
    trimVal(user?.details?.department) ||
    trimVal(user?.details?.department_name) ||
    trimVal(user?.department);

  const profileDesig =
    trimVal(user?.details?.sap_department_designation_wings?.designation_name) ||
    trimVal(user?.details?.designation) ||
    trimVal(user?.designation);

  const profilePayScale =
    trimVal(user?.details?.pay_scale) ||
    trimVal(user?.details?.payscale) ||
    trimVal(user?.pay_scale) ||
    trimVal(user?.payscale);

  const profileBasicPay =
    trimVal(user?.details?.basicSalary) ||
    trimVal(user?.details?.basicPay) ||
    trimVal(user?.details?.basic_pay) ||
    trimVal(user?.basicSalary) ||
    trimVal(user?.basicPay);

  const profileJoiningDate = formatProfileDate(
    user?.details?.date_of_joining ||
      user?.details?.dateOfJoining ||
      user?.details?.joining_date ||
      user?.date_of_joining ||
      user?.dateOfJoining
  );

  const profileEmploymentType =
    trimVal(user?.details?.employmentType) || trimVal(user?.employmentType);

  return {
    Name: profileName,
    EmployeeNumber: profileEmpId,
    BirthDate: profileBirthDate,
    Wing: profileWing,
    Department: profileDept,
    Designation: profileDesig,
    PayScale: profilePayScale,
    BasicPay: profileBasicPay,
    EmploymentType: profileEmploymentType || NOT_SPECIFIED,
    JoiningDate: profileJoiningDate,
  };
}

export function extractOfficerAndRows(raw: any): { officer: any; rows: any[] } {
  let node = raw;
  let guard = 0;
  while (
    node &&
    typeof node === "object" &&
    !Array.isArray(node) &&
    node.data !== undefined &&
    node.EmployeeName === undefined &&
    node.EmpID === undefined &&
    node.ToTable === undefined
  ) {
    node = node.data;
    if (++guard > 8) break;
  }

  if (Array.isArray(node)) {
    return { officer: node[0] ?? null, rows: node };
  }

  if (node && typeof node === "object") {
    const results =
      node?.ToTable?.results ??
      node?.toTable?.results ??
      node?.results ??
      (Array.isArray(node?.ToTable) ? node.ToTable : null);
    return {
      officer: node,
      rows: Array.isArray(results) ? results : [],
    };
  }

  return { officer: null, rows: [] };
}

export function mapPropertyRows(rawRows: any[]): PropertyRow[] {
  return (rawRows || [])
    .filter(
      (it: any) =>
        trimVal(it.SequenceNumber) ||
        trimVal(it.TypeOfProperty) ||
        trimVal(it.NameDetailsAndLocationOfProperty)
    )
    .map((it: any) => ({
      __uid: genUid(),
      SequenceNumber: trimVal(it.SequenceNumber),
      TypeOfProperty: trimVal(it.TypeOfProperty),
      NameDetailsAndLocationOfProperty: trimVal(it.NameDetailsAndLocationOfProperty),
      DetailsOfProperty: trimVal(it.DetailsOfProperty),
      SizeAndArea: trimVal(it.SizeAndArea),
      AcquisitionDateInDDMMYYYYFormatOnly: trimVal(it.AcquisitionDateInDDMMYYYYFormatOnly),
      AcquisitionPrice: trimVal(it.AcquisitionPrice),
      PresentValue: trimVal(it.PresentValue),
      OwnerNameWithRelationshipAndShareInPrope: trimVal(
        it.OwnerNameWithRelationshipAndShareInPrope
      ),
      ModeOfAcquisition: trimVal(it.ModeOfAcquisition),
      AnnualIncomeFromProperty: trimVal(it.AnnualIncomeFromProperty),
      Remarks: trimVal(it.Remarks),
    }));
}

export function buildOfficerFromApi(officerObj: any, profile: OfficerDetails, user = getLoggedInUser()): OfficerDetails {
  const source = officerObj || {};
  return {
    Name: trimVal(source.EmployeeName) || profile.Name,
    EmployeeNumber: trimVal(source.EmpID) || profile.EmployeeNumber,
    BirthDate: formatProfileDate(source.DateOfBirth) || profile.BirthDate,
    Wing: trimVal(source.Wing) || profile.Wing,
    Department: trimVal(source.Department) || profile.Department,
    Designation: trimVal(source.Designation) || profile.Designation,
    PayScale: trimVal(source.Payscale) || profile.PayScale,
    BasicPay: trimVal(source.BasicPay) || profile.BasicPay,
    EmploymentType:
      trimVal(user?.details?.employmentType) ||
      trimVal(user?.employmentType) ||
      trimVal(source.NatureOfEmployment) ||
      profile.EmploymentType ||
      NOT_SPECIFIED,
    JoiningDate: formatProfileDate(source.DateOfJoining) || profile.JoiningDate,
  };
}

type ApiResult = { success: boolean; message: string; data: any };

export function unwrapApi(res: any): ApiResult {
  const nested = res?.data?.data;
  if (nested && (nested.status === 200 || nested.status === 201) && nested.data !== undefined) {
    return {
      success: true,
      message: res?.data?.message || nested.message || "Success",
      data: nested.data,
    };
  }

  const layers = [res?.data, res].filter((layer) => layer && typeof layer === "object");
  for (const layer of layers) {
    if (layer.status === 200 || layer.status === 201 || layer.success === true) {
      return {
        success: true,
        message: layer.message || "Success",
        data: layer.data,
      };
    }
  }

  return {
    success: false,
    message: res?.data?.message || res?.message || "Something went wrong",
    data: res?.data?.data ?? res?.data ?? null,
  };
}

export function apiErrorMessage(err: any, fallback: string): string {
  return (
    err?.response?.data?.data?.message ||
    err?.response?.data?.message ||
    Api.getErrorMessage(err) ||
    err?.message ||
    fallback
  );
}

export function buildPayloadProperties(rows: PropertyRow[], empId: string) {
  return rows.map((p, i) => ({
    SequenceNumber: p.SequenceNumber || String(i + 1).padStart(5, "0"),
    EmpID: empId,
    TypeOfProperty: p.TypeOfProperty,
    NameDetailsAndLocationOfProperty: p.NameDetailsAndLocationOfProperty,
    DetailsOfProperty: p.DetailsOfProperty,
    SizeAndArea: p.SizeAndArea,
    AcquisitionPrice: p.AcquisitionPrice,
    AcquisitionDateInDDMMYYYYFormatOnly: p.AcquisitionDateInDDMMYYYYFormatOnly,
    PresentValue: p.PresentValue,
    OwnerNameWithRelationshipAndShareInPrope: p.OwnerNameWithRelationshipAndShareInPrope,
    ModeOfAcquisition: p.ModeOfAcquisition,
    ...(p.AnnualIncomeFromProperty != "" && {
      AnnualIncomeFromProperty: p.AnnualIncomeFromProperty,
    }),
    Remarks: p.Remarks,
  }));
}

const APR_ROUTES = {
  GET: "sap-annual-property-returns/get-details",
  NIL: "sap-annual-property-returns/get-nil-details",
  SYNC: "sap-annual-property-returns/sync",
  SAVE: "sap-annual-property-returns/save",
  DOWNLOAD: "sap-annual-property-returns/download",
};

// Same envelope the app sends: Header.url is the route only (not /api/...),
// and the payload sits in Request.data.
async function postLikeApp(route: string, payload: any, actionFrom = ""): Promise<any> {
  const token = tokenService.getToken();
  const authHeader = token
    ? token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
    : undefined;

  const body = {
    Header: {
      device_id: "WEB",
      device_token: "WEB",
      device_type: "WEB",
      guid:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      epoch: Date.now(),
      url: route,
      service_request_id: route,
      action: actionFrom,
    },
    Request: { data: payload },
  };

  const url = `${API_URLS.ANNUAL_PROPERTY_RETURN.GET.split("sap-annual-property-returns/")[0]}${route}`;
  try {
    const response = await axios.post(url, body, {
      headers: {
        "Content-Type": "application/json",
        "enc-req": "0",
        "enc-res": "0",
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      timeout: 120000,
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.data) return err.response.data;
    throw err;
  }
}

export async function fetchPropertyDetails(empId: string): Promise<ApiResult> {
  const res = await postLikeApp(APR_ROUTES.GET, { empId });
  return unwrapApi(res);
}

export async function fetchNilPropertyDetails(empId: string): Promise<ApiResult> {
  const res = await postLikeApp(APR_ROUTES.NIL, { empId });
  return unwrapApi(res);
}

export async function syncAnnualProperty(empId: string): Promise<ApiResult> {
  const res = await postLikeApp(APR_ROUTES.SYNC, { empId });
  return unwrapApi(res);
}

export async function saveAnnualProperty(payload: any): Promise<ApiResult> {
  const res = await postLikeApp(APR_ROUTES.SAVE, payload);
  return unwrapApi(res);
}

function sanitizeBase64(content: any): string | null {
  if (content == null) return null;
  const str = typeof content === "string" ? content : null;
  if (!str) return null;
  let cleaned = str.replace(/^data:([^;]+);base64,/i, "").replace(/\s/g, "");
  cleaned = cleaned.replace(/-/g, "+").replace(/_/g, "/");
  return cleaned.length > 0 ? cleaned : null;
}

function base64ToBlob(base64: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
}

function fileNameFromDisposition(header: string | undefined, fallback: string): string {
  const raw = header
    ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
    ?.replace(/['"]/g, "")
    ?.trim();
  return raw || fallback;
}

function normalizePdfName(name: string, empId: string): string {
  let safe =
    name.split("/").pop()?.replace(/\s+/g, "_") || `Annual_Property_Return_${empId}.pdf`;
  const ext = safe.includes(".") ? safe.split(".").pop() : "pdf";
  if (!ext || ext === safe) safe = `${safe.replace(/\.[^.]*$/, "")}.pdf`;
  if (!safe.toLowerCase().endsWith(".pdf")) safe = `${safe.replace(/\.[^.]*$/, "")}.pdf`;
  return safe;
}

export async function downloadAnnualPropertyPdf(
  empId: string
): Promise<{ blob: Blob; fileName: string }> {
  const token = tokenService.getToken();
  const authHeader = token
    ? token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`
    : undefined;

  const body = {
    Header: {
      device_id: "WEB",
      device_token: "WEB",
      device_type: "WEB",
      guid:
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      epoch: Date.now(),
      url: APR_ROUTES.DOWNLOAD,
      service_request_id: APR_ROUTES.DOWNLOAD,
      action: "AnnualPropertyDownload",
    },
    Request: { data: { empId } },
  };

  const response = await axios.post(API_URLS.ANNUAL_PROPERTY_RETURN.DOWNLOAD, body, {
    headers: {
      "Content-Type": "application/json",
      "enc-req": "0",
      "enc-res": "0",
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    responseType: "blob",
    timeout: 120000,
  });

  const blob: Blob = response.data;
  const contentType = String(response?.headers?.["content-type"] || blob.type || "").toLowerCase();
  const fallbackName = `Annual_Property_Return_${empId}.pdf`;
  let fileName = normalizePdfName(
    fileNameFromDisposition(response?.headers?.["content-disposition"], fallbackName),
    empId
  );

  const isPdfBinary =
    (contentType.includes("application/pdf") || contentType.includes("application/octet-stream")) &&
    blob.size > 0;

  if (isPdfBinary) return { blob, fileName };

  const text = await blob.text();
  let parsed: any = {};
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Invalid response from server");
  }

  const resData = parsed?.data ?? parsed;
  if (parsed?.data?.status === 400 && parsed?.data?.message) {
    throw new Error(parsed.data.message);
  }
  if (parsed?.message && !parsed.success && parsed.status === 400) {
    throw new Error(parsed.message);
  }

  const data = resData?.data ?? resData;
  const dataObj = Array.isArray(data) ? data[0] : data;
  const content =
    dataObj?.Content ??
    dataObj?.content ??
    dataObj?.FileContent ??
    dataObj?.fileContent ??
    dataObj?.pdfBase64 ??
    dataObj?.base64 ??
    dataObj?.file ??
    dataObj?.data ??
    (typeof resData === "string" ? resData : undefined) ??
    (typeof data === "string" ? data : undefined);

  const fromJsonFileName =
    dataObj?.FileName ?? dataObj?.fileName ?? dataObj?.name ?? dataObj?.documentName;
  if (fromJsonFileName) fileName = normalizePdfName(String(fromJsonFileName), empId);

  const clean = sanitizeBase64(content);
  if (!clean) throw new Error(parsed?.message || parsed?.data?.message || "No file content in response.");
  return { blob: base64ToBlob(clean), fileName };
}

export function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const REQUIRED_FIELDS: (keyof PropertyRow)[] = [
  "TypeOfProperty",
  "NameDetailsAndLocationOfProperty",
  "DetailsOfProperty",
  "SizeAndArea",
  "AcquisitionPrice",
  "AcquisitionDateInDDMMYYYYFormatOnly",
  "PresentValue",
  "OwnerNameWithRelationshipAndShareInPrope",
  "ModeOfAcquisition",
];

export function validateDraft(draft: PropertyRow): Record<string, boolean> {
  const errs: Record<string, boolean> = {};
  REQUIRED_FIELDS.forEach((field) => {
    if (!trimVal(draft[field])) errs[field] = true;
  });
  return errs;
}
